import { api } from "@/api";
import { useApiMutate } from "@/api/hooks/useApiMutate";
import { useStore } from "@/store";
import type { StackProps } from "@chakra-ui/react";
import { Button, HStack, Input, Stack, Text } from "@chakra-ui/react";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { PromoCodeError } from "./PromoCodeError";
import { telemetry } from "@/telemetry";

interface Props extends StackProps {
  colorScheme: "dark" | "light";
  showError?: boolean;
  onApiError?: (isApiError: boolean) => void;
}

export const PromoCodePanel = observer(
  ({ colorScheme, showError = true, onApiError, ...props }: Props) => {
    const { orderData, setOrderData } = useStore();
    const [promoCode, setPromoCode] = useState("");

    const apiSubmit = useApiMutate({
      mutationFn: api.getPromoCode,
      onSuccess: (data) => {
        telemetry.trackEvent("promo_code_applied", {
          result: "ok",
          discount_percent: data.discountAmount,
          code: data.code,
        });
        setOrderData({
          ...orderData,
          promotionalDiscountAmount: data.discountAmount,
          promotionalCode: data.code,
        });
        onApiError?.(false);
      },
      onError: (error) => {
        telemetry.trackEvent("promo_code_applied", {
          result: error.response?.status === 404 ? "not_found" : "error",
        });
        setPromoCode("");
        setOrderData({
          ...orderData,
          promotionalDiscountAmount: 0,
          promotionalCode: "",
        });
        onApiError?.(true);
      },
    });

    const handleApply = () => apiSubmit.mutate(promoCode);

    return (
      <Stack {...props}>
        <Text
          fontWeight="bold"
          color={colorScheme === "dark" ? "black" : undefined}
        >
          Promotional code:
        </Text>
        <HStack>
          <Input
            size="sm"
            maxWidth="225px"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
            placeholder="Enter promo code"
            borderWidth="1px"
            borderColor={colorScheme === "dark" ? "gray.300" : undefined}
          />
          <Button
            isDisabled={!promoCode}
            onClick={handleApply}
            isLoading={apiSubmit.isPending}
            minWidth="120px"
          >
            Apply code
          </Button>
        </HStack>

        {apiSubmit.isError && showError && <PromoCodeError ml="auto" />}
      </Stack>
    );
  },
);
